import { useTrialApplication } from '@components/hooks/TrialApplicationQuestionnaireProvider/index.client'
import { Text } from '@components/primitives/Text'
import type CheckValue from '@components/primitives/ToggleButton/EligibilityToggleGroup/EligibilityToggleValue'
import getEligibilityCriteriaFieldKey from '@lib/questionnaire/trialApplication/eligibilityCriteria/getEligibilityCriteriaFieldKey'
import getParsedEligibilityCriteriaForTrial from '@modules/trials/helpers/parseEligibilityCriteria/getParsedEligibilityCriteriaForTrial'
import type { TrialAssociation } from '@modules/trials/types/TrialAssociation'
import { ColorfulEligibilityCriterion } from './ColorfulEligibilityCriterion'

interface Criteria {
  id?: number | null
  label: string
  status?: CheckValue
}

export const ColorfulEligibilityCriteria = ({
  trial,
}: {
  trial: TrialAssociation
}) => {
  const { getTrialApplicationQuestionnaire } = useTrialApplication()

  const criteria = getParsedEligibilityCriteriaForTrial(trial)

  const initialEligibilityAnswers = criteria.map((crit): Criteria => {
    const trialApplication = trial.slug
      ? getTrialApplicationQuestionnaire(trial.slug)
      : undefined
    const userProvidedAnswer =
      crit.id && trialApplication
        ? (trialApplication[
            getEligibilityCriteriaFieldKey(crit.id, crit.isInclusion)
          ] as CheckValue)
        : undefined

    return { id: crit.id, label: crit.criterion, status: userProvidedAnswer }
  })

  const inclusionCriteria = criteria.filter((c) => c.isInclusion)
  const exclusionCriteria = criteria.filter((c) => !c.isInclusion)

  if (inclusionCriteria.length === 0 && exclusionCriteria.length === 0) {
    return null
  }

  return (
    <div className='rounded-2xl border border-neutral200 p-6'>
      {inclusionCriteria.length > 0 && (
        <>
          <Text
            className='text-neutral900'
            element={'p'}
            styleName='p-strong'
            value='Inclusion Criteria'
          />
          <Text
            className='text-neutral600'
            styleName='p-small'
            value='You may be eligible if you check “Yes” for the criteria below'
          />
          <div className='mt-4 grid grid-cols-1 gap-4 md:mt-6'>
            {criteria
              .filter((c) => c.isInclusion)
              .map((eligibilityCriterion) => (
                <div
                  className='border-b border-neutral200 last:border-b-0'
                  key={`inclusionCriteria-${eligibilityCriterion.id}`}
                >
                  <ColorfulEligibilityCriterion
                    criterion={eligibilityCriterion}
                    defaultValue={
                      initialEligibilityAnswers.find(
                        (crit) => crit.id === eligibilityCriterion.id,
                      )?.status
                    }
                    slug={trial.slug}
                  />
                </div>
              ))}
          </div>
        </>
      )}
      {exclusionCriteria.length > 0 && (
        <>
          <Text
            className='mt-6 text-neutral900'
            element={'p'}
            styleName='p-strong'
            value='Exclusion Criteria'
          />
          <Text
            className='text-neutral600'
            styleName='p-small'
            value='You may be eligible for the trial if you check “No” for criteria below:'
          />
          <div className='mt-4 grid grid-cols-1 gap-4 md:mt-6'>
            {exclusionCriteria.map((eligibilityCriterion) => (
              <div
                className='border-b border-neutral200 last:border-b-0'
                key={`exclusionCriteria-${eligibilityCriterion.id}`}
              >
                <ColorfulEligibilityCriterion
                  criterion={eligibilityCriterion}
                  defaultValue={
                    initialEligibilityAnswers.find(
                      (crit) => crit.id === eligibilityCriterion.id,
                    )?.status
                  }
                  slug={trial.slug}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
